<script setup lang="ts">
import { computed, PropType } from "vue";
import Galleria from "primevue/galleria";
import {
    BannerAudience,
    BannerViewer,
    isMobileView,
    matchesBannerAudience,
    resolveBannerAudience,
    StartPageBannerItem,
} from "@persolog/utils";

const props = defineProps({
    /**
     * The configured banners, already resolved for the viewer's language and with their image urls
     * on this app's own origin. Empty until the api has answered, and empty for good when nothing is
     * configured — in which case the bundled hero below is what shows (RDT-359).
     */
    banners: {
        type: Array as PropType<StartPageBannerItem[]>,
        default: () => [],
    },
    // Interface language of the (possibly anonymous) visitor, e.g. "de".
    locale: {
        type: String,
        required: true,
    },
    // Logged-in trainer, or null before login.
    trainer: {
        type: Object as PropType<BannerViewer | null>,
        default: null,
    },
});

/**
 * What shows when language-management has no start-page banner configured — the permanent hero.
 *
 * Deliberately kept in the app rather than migrated into the database: the start page must never be
 * a blank band, not while the api is still answering, not if it fails, and not before marketing has
 * entered anything. The files live in EACH app's public/images/banner/ and are the same two the
 * hardcoded list used, re-encoded from 975 KB / 3.0 MB of jpeg to ~125 KB of WebP each (RDT-359).
 */
const FALLBACK: StartPageBannerItem = {
    id: 0,
    desktopSrc: "./images/banner/hero_presentation.webp",
    mobileSrc: "./images/banner/hero_presentation-mobile.webp",
    alt: "persolog",
    audience: "ALL",
};

const responsiveOptions = [
    {
        breakpoint: "991px",
        numVisible: 4,
    },
    {
        breakpoint: "767px",
        numVisible: 3,
    },
    {
        breakpoint: "575px",
        numVisible: 1,
    },
];

// Logged-in trainers see the set of their distributor; anonymous visitors the one matching
// their interface language. Shared with the notification banner so the two cannot drift (RDT-339).
const audience = computed<BannerAudience>(() => resolveBannerAudience(props.locale, props.trainer));

/**
 * The banners this viewer is addressed by, or the fallback when that leaves nothing. Note the
 * fallback also covers "everything configured is aimed at the other audience", which is the case a
 * naive `banners.length === 0` check would miss.
 */
const visibleBanners = computed<StartPageBannerItem[]>(() => {
    const addressed = props.banners.filter((banner) => matchesBannerAudience(banner.audience, audience.value));
    return addressed.length > 0 ? addressed : [FALLBACK];
});

function getImages() {
    const isMobile = isMobileView();
    return visibleBanners.value.map((banner) => ({
        itemImageSrc: isMobile ? banner.mobileSrc : banner.desktopSrc,
        alt: banner.alt,
        link: banner.link ?? undefined,
    }));
}
</script>

<template>
    <Galleria
        :value="getImages()"
        :responsive-options="responsiveOptions"
        :num-visible="5"
        container-class="gallery-cover"
        :circular="true"
        :show-thumbnails="false"
        :show-indicators="false"
        :show-item-navigators="false"
        :show-item-navigators-on-hover="true"
        :show-indicators-on-item="true"
        :auto-play="true"
        :transition-interval="8000"
    >
        <template #item="slotProps">
            <!-- A banner without a link is not a link: an <a> with no href is focusable, announced as
                 a link and leads nowhere, which is exactly what the permanent hero used to render. -->
            <a
                v-if="slotProps.item.link"
                :href="slotProps.item.link"
                target="_blank"
                rel="noopener noreferrer"
                class="gallery-link"
            >
                <img :src="slotProps.item.itemImageSrc" :alt="slotProps.item.alt" class="gallery-cover-image" />
            </a>
            <span v-else class="gallery-link">
                <img :src="slotProps.item.itemImageSrc" :alt="slotProps.item.alt" class="gallery-cover-image" />
            </span>
        </template>
    </Galleria>
</template>

<style scoped>
.custom-background {
    background-color: var(--p-surface-0);
}

.gallery-cover {
    --gallery-height: 33vh;
    width: 100%;
    max-width: 100vw;
    height: var(--gallery-height);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

@media (max-width: 767px) {
    .gallery-cover {
        --gallery-height: 43vh;
    }
}
.gallery-cover-image {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
}
.gallery-cover .p-galleria-header {
    background-color: transparent;
    display: flex;
    justify-content: space-around;
    align-items: center;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1;
    top: 0;
    left: 0;
}

.gallery-cover .p-galleria-header .gallery-cover-box {
    max-width: 33vw;
}

.gallery-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
}

/* Styles for PrimeVue Galleria to fit image height */
.gallery-cover.p-galleria {
    height: auto !important;
}

.gallery-cover .p-galleria-content {
    height: auto !important;
}

.gallery-cover .p-galleria-item-wrapper {
    height: auto !important;
}

.gallery-cover .p-galleria-item {
    height: auto !important;
}
</style>
